import type { AuthTokens } from '@nexo/types';
import { ApiError } from './errors.js';

export interface TokenStore {
  get(): AuthTokens | null;
  set(tokens: AuthTokens): void;
  clear(): void;
}

export interface HttpClientOptions {
  baseUrl: string;
  tokens: TokenStore;
  /** Called when the session cannot be refreshed (user must log in again). */
  onSessionExpired?: () => void;
  fetch?: typeof fetch;
  /** Sent with every request (e.g. which app is calling, for the active sessions list). */
  headers?: Record<string, string>;
}

type Query = Record<string, string | number | boolean | undefined | null>;

export interface RequestOptions {
  body?: unknown;
  query?: Query;
  /** Skip the Authorization header and the refresh-on-401 logic. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/**
 * Minimal fetch wrapper: JSON in/out, bearer auth, and a single-flight token refresh
 * when the API answers 401 (concurrent requests share one refresh call).
 */
export class HttpClient {
  private refreshing: Promise<boolean> | null = null;
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly options: HttpClientOptions) {
    this.fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  get baseUrl(): string {
    return this.options.baseUrl;
  }

  get tokens(): TokenStore {
    return this.options.tokens;
  }

  async request<T>(method: HttpMethod, path: string, opts: RequestOptions = {}): Promise<T> {
    let response = await this.send(method, path, opts);
    if (response.status === 401 && !opts.anonymous && this.options.tokens.get()) {
      if (await this.refresh()) {
        response = await this.send(method, path, opts);
      } else {
        this.options.tokens.clear();
        this.options.onSessionExpired?.();
      }
    }
    return this.parse<T>(response);
  }

  /** Exchanges the refresh token for a new pair. Returns false if the session is gone. */
  refresh(): Promise<boolean> {
    this.refreshing ??= this.doRefresh().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async doRefresh(): Promise<boolean> {
    const current = this.options.tokens.get();
    if (!current) return false;
    try {
      const response = await this.send('POST', '/auth/refresh', {
        anonymous: true,
        body: { refreshToken: current.refreshToken },
      });
      if (!response.ok) return false;
      this.options.tokens.set((await response.json()) as AuthTokens);
      return true;
    } catch {
      // Network failure: keep tokens so a later retry can succeed.
      return false;
    }
  }

  private send(method: HttpMethod, path: string, opts: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = {
      ...this.options.headers,
      Accept: 'application/json',
    };
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    const token = opts.anonymous ? null : this.options.tokens.get()?.accessToken;
    if (token) headers.Authorization = `Bearer ${token}`;

    const init: RequestInit = { method, headers };
    if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
    if (opts.signal) init.signal = opts.signal;
    return this.fetchImpl(this.url(path, opts.query), init);
  }

  /** Absolute URL for an API path (used by the multipart uploader). */
  url(path: string, query?: Query): string {
    const url = new URL(`${this.options.baseUrl.replace(/\/$/, '')}/api${path}`);
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    }
    return url.toString();
  }

  private async parse<T>(response: Response): Promise<T> {
    const text = await response.text();
    const data: unknown = text ? JSON.parse(text) : undefined;
    if (!response.ok) throw new ApiError(response.status, data);
    return data as T;
  }
}
